<i18n src="./resources/locales.json"></i18n>

<template>
  <EcsSideBarWrapper
    class="settings-side-bar"
    :search-placeholder="$t('searchPlaceholder')"
    @search-input-changed="handleSearch"
  >
    <v-row no-gutters>
      <v-col>
        <!-- Pending interactions -->
        <EcsSideBarInteractionList
          v-if="displayPendingInteraction"
          :header="$t('pendingInteractions')"
          :interactions="getPendingInteractions"
          is-pending
          show-counter
        />
        <EcsSideBarTileContent
          v-for="(value, key) in filteredSettings"
          :key="key"
          ref="settingGroup"
          :header="$t(`${key}.company`)"
        >
          <v-list-item
            v-for="setting in value"
            :key="setting.id"
            ref="setting"
            :tooltip="!!setting.titleTooltipI18nKey"
          >
            <v-list-item-content
              class="item-content"
              :class="{
                'pa-0': ['dropdown', 'searchDropdown', 'toggle'].includes(setting.type)
              }"
            >
              <span v-if="!!setting.titleTooltipI18nKey" class="tooltip-message elevation-1">
                {{ $t(`${key}.${setting.titleTooltipI18nKey}`) }}
              </span>

              <v-list-item-title
                v-if="setting.type !== 'dropdown' || setting.type !== 'searchDropdown'"
                :class="{ 'slider-inside': setting.type === 'slider' }"
                class="settings-content"
              >
                <span
                  v-show="!['dropdown', 'searchDropdown'].includes(setting.type)"
                  :title="$t(`${key}.${setting.titleI18nKey}`)"
                >
                  {{ $t(`${key}.${setting.titleI18nKey}`) }}
                </span>
                <span v-show="setting.type === 'slider'"> ({{ setting.value || '0' }}) </span>
              </v-list-item-title>

              <v-slider
                v-if="setting.type === 'slider'"
                class="slider"
                color="primary"
                :value="setting.value"
                append-icon="mdi-volume-high"
                prepend-icon="mdi-volume-medium"
                @end="value => saveSetting(value, setting.action)"
              />

              <v-select
                v-if="setting.type === 'dropdown'"
                v-model="setting.value"
                class="dropdown"
                :label="$t(`${key}.${setting.titleI18nKey}`)"
                :items="setting.items"
                :sidebar-input="true"
                :disabled="!setting.items.length"
                @change="value => saveSetting(value, setting.action)"
              >
                <template slot="selection" slot-scope="data">
                  <span>{{ data.item.i18nKey ? $t(data.item.i18nKey) : data.item.label }}</span>
                </template>
                <template slot="item" slot-scope="data">
                  <span>{{ data.item.i18nKey ? $t(data.item.i18nKey) : data.item.label }}</span>
                </template>
              </v-select>

              <v-autocomplete
                v-if="setting.type === 'searchDropdown'"
                v-model="setting.value"
                class="dropdown"
                :item-text="setting.itemText"
                :label="$t(`${key}.${setting.titleI18nKey}`)"
                :items="setting.items"
                :menu-props="{ contentClass: 'custom-scrollbar' }"
                :disabled="!setting.items.length"
                editable
                :return-object="setting.returnObject || false"
                @change="value => saveSetting(value, setting.action)"
              />
            </v-list-item-content>

            <v-list-item-action v-if="setting.type !== 'dropdown' && setting.type !== 'searchDropdown'">
              <v-switch
                v-if="setting.type === 'toggle'"
                v-model="setting.value"
                @change="value => saveSetting(value, setting.action)"
              />
              <v-btn
                v-else-if="setting.type === 'button'"
                text
                icon
                ripple
                :disabled="setting.disabled"
                @click.stop="setting.action && setting.action()"
              >
                <v-icon>mdi-launch</v-icon>
              </v-btn>
            </v-list-item-action>
            <v-list-item-action v-if="setting.appendIcon && setting.value !== 'silent'">
              <v-btn icon @click="setting.appendAction(setting.id)">
                <v-icon button>{{ setting.appendIcon }}</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </EcsSideBarTileContent>
      </v-col>
    </v-row>

    <EcsEndpointSelectionDialogContainer ref="endpointSelectionDialog" />
    <EcsConfirmationDialog
      ref="languageConfirmationDialog"
      :title="$t('dialogs.languageConfirmation.title')"
      :message="$t('dialogs.languageConfirmation.message')"
      :confirm-button-text="$t('dialogs.languageConfirmation.confirm')"
      :cancel-button-text="$t('dialogs.languageConfirmation.later')"
    />
  </EcsSideBarWrapper>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EcsSideBarPendingInteractionMixin from '@/mixins/ecs-side-bar-pending-interaction-mixin/EcsSideBarPendingInteractionMixin';
import EcsEndpointSelectionDialogContainer from '@/components/ecs-endpoint-selection-dialog/EcsEndpointSelectionDialogContainer';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';

const VOLUME_HIGH_ICON = 'mdi-volume-high';

export default {
  name: 'EcsSettingsSideBar',

  components: {
    EcsEndpointSelectionDialogContainer,
    EcsConfirmationDialog
  },

  mixins: [EcsSideBarPendingInteractionMixin],

  data() {
    return {
      searchString: ''
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('BUSINESS_PROCESSES', ['availableBusinessProcesses']),

    ...mapGetters('REFERENCES', [
      'availableInteractionHistoryPeriods',
      'availableAlertsEndOfBreak',
      'availableLanguages',
      'availableTimeFormats',
      'availableTimeZones',
      'availableIncomingInteractionDisplayModes',
      'availableIdleTimesToAgentIntelligenceCenter',
      'availableIncomingInteractionRingtones',
      'getBusinessProcessesDIDs'
    ]),

    ...mapGetters('SETTINGS', [
      'incomingInteractionChatRingtone',
      'incomingInteractionCallRingtone',
      'incomingInteractionEmailRingtone',
      'currentUserSettings',
      'incomingInteractionSmsRingtone'
    ]),

    liveKPIs() {
      return this.preferencesSettings.pauseKPIEvents === false;
    },

    availableSettings() {
      const { preferencesSettings, applicationSettings, agentSettings } = this.currentUserSettings;

      return {
        preferencesSettings: [
          {
            id: 'incomingInteractionDisplay',
            titleI18nKey: 'incomingInteractionDisplay',
            type: 'dropdown',
            value: preferencesSettings.incomingInteractionDisplay,
            items: this.availableIncomingInteractionDisplayModes,
            action: this.updateIncomingInteractionDisplay
          },
          {
            id: 'incomingInteractionAutoPlayRecordings',
            titleI18nKey: 'incomingInteractionAutoPlayRecordings',
            type: 'toggle',
            value: preferencesSettings.autoPlayRecordings,
            action: this.updateAutoPlayRecordings
          },
          {
            id: 'interactionHistoryPeriod',
            titleI18nKey: 'interactionHistoryPeriod',
            type: 'dropdown',
            value: preferencesSettings.interactionHistoryPeriod,
            items: this.availableInteractionHistoryPeriods,
            action: this.updateInteractionHistoryPeriod
          },
          {
            id: 'incomingInteractionRingtones.call',
            titleI18nKey: 'incomingCallRingtone',
            type: 'dropdown',
            appendIcon: VOLUME_HIGH_ICON,
            appendAction: this.playRingtone,
            value: preferencesSettings.incomingInteractionRingtones.call || 'silent',
            items: this.incomingInteractionRingtones,
            action: this.updateCallIncomingInteractionRingtones
          },
          {
            id: 'incomingInteractionRingtones.chat',
            titleI18nKey: 'incomingChatRingtone',
            type: 'dropdown',
            appendIcon: VOLUME_HIGH_ICON,
            appendAction: this.playRingtone,
            value: preferencesSettings.incomingInteractionRingtones.chat || 'silent',
            items: this.incomingInteractionRingtones,
            action: this.updateChatIncomingInteractionRingtones
          },
          {
            id: 'incomingInteractionRingtones.email',
            titleI18nKey: 'incomingEmailRingtone',
            type: 'dropdown',
            appendIcon: VOLUME_HIGH_ICON,
            appendAction: this.playRingtone,
            value: preferencesSettings.incomingInteractionRingtones.email || 'silent',
            items: this.incomingInteractionRingtones,
            action: this.updateEmailIncomingInteractionRingtones
          },
          {
            id: 'incomingInteractionRingtones.sms',
            titleI18nKey: 'incomingSmsRingtone',
            type: 'dropdown',
            appendIcon: VOLUME_HIGH_ICON,
            appendAction: this.playRingtone,
            value: preferencesSettings.incomingInteractionRingtones.sms || 'silent',
            items: this.incomingInteractionRingtones,
            action: this.updateSmsIncomingInteractionRingtones
          },
          {
            id: 'idleTimeToAgentIntelligenceCenter',
            titleI18nKey: 'idleTimeToAgentIntelligenceCenter',
            type: 'dropdown',
            value: preferencesSettings.idleTimeToAgentIntelligenceCenter,
            items: this.availableIdleTimesToAgentIntelligenceCenter,
            action: this.updateIdleTimeToAgentIntelligenceCenter
          },
          {
            id: 'alertEndOfBreak',
            titleI18nKey: 'alertEndOfBreakTitle',
            type: 'dropdown',
            value: preferencesSettings.alertEndOfBreak,
            items: this.availableAlertsEndOfBreak,
            action: this.updateAlertEndOfBreak
          },
          {
            id: 'liveKPIEvents',
            titleI18nKey: 'liveKPIEvents',
            type: 'toggle',
            value: this.liveKPIs,
            action: this.updatePauseKPIEvents
          },
          {
            id: 'muteDTMF',
            titleI18nKey: 'muteDTMF',
            type: 'toggle',
            value: preferencesSettings.muteDTMF,
            action: this.updateMuteDTMF
          },
          {
            id: 'incomingInteractionAutoOffHookState',
            titleI18nKey: 'incomingInteractionAutoOffHookState',
            titleTooltipI18nKey: 'incomingInteractionAutoOffHookStateTooltip',
            type: 'toggle',
            value: preferencesSettings.autoOffHookState,
            action: this.updateAutoOffHookState
          },
          {
            id: 'autoOpenInteractionDetails',
            titleI18nKey: 'autoOpenInteractionDetails',
            titleTooltipI18nKey: 'autoOpenInteractionDetailsTooltip',
            type: 'toggle',
            value: preferencesSettings.autoOpenInteractionDetails,
            action: this.updateAutoOpenInteractionDetails
          },
          {
            id: 'showAppCurrentTime',
            titleI18nKey: 'showAppCurrentTime',
            type: 'toggle',
            value: preferencesSettings.showAppCurrentTime,
            action: this.updateShowAppCurrentTime
          }
        ],
        applicationSettings: [
          {
            id: 'language',
            titleI18nKey: 'selectLanguage',
            type: 'dropdown',
            value: applicationSettings.language,
            items: this.availableLanguages,
            action: this.updateUserLanguage
          },
          {
            id: 'timeFormat',
            titleI18nKey: 'selectTimeFormat',
            type: 'dropdown',
            value: applicationSettings.timeFormat,
            items: this.availableTimeFormats,
            action: this.updateTimeFormat
          },
          {
            id: 'timeZone',
            titleI18nKey: 'selectTimeZone',
            type: 'searchDropdown',
            itemText: 'standardName',
            value: applicationSettings.timeZoneId,
            items: this.availableTimeZones,
            action: args => {
              this.updateTimeZone(args);
            },
            returnObject: true
          }
        ],
        // HIDE AUDIO currentUserSettings.
        /* audioSettings: [
          {
            id: 'speakerVolume',
            titleI18nKey: 'sliderSpeakerVolume',
            type: 'slider',
            value: this.audioSettings.speakerVolume
          },
          {
            id: 'microphoneVolume',
            titleI18nKey: 'sliderMicrophoneVolume',
            type: 'slider',
            value: this.audioSettings.microphoneVolume
          },
          {
            id: 'deviceSettings',
            titleI18nKey: 'popUpBtnEditDeviceSettings',
            type: 'button',
            value: this.audioSettings.deviceSettings
          }
        ], */
        agentSettings: [
          {
            id: 'defaultOutgoingCallBusinessProcessId',
            titleI18nKey: 'defaultOutgoingCallBusinessProcess',
            type: 'dropdown',
            value: agentSettings.defaultOutgoingCallBusinessProcessId,
            items: this.availableBusinessProcesses.map(bp => ({ label: bp.name, value: bp.id })),
            action: this.updateDefaultOutgoingCallBusinessProcessId
          },
          {
            id: 'defaultCallingLineIdentificationPerBP',
            titleI18nKey: 'defaultCallingLineIdentificationPerBP',
            type: 'searchDropdown',
            value: this.defaultCallingLineIdentificationPerBP,
            items: this.businessProcessesDIDs.map(bp => ({
              label: bp.name ? `${bp.did} (${bp.name})${bp.isDefault ? ' • ORG Default' : ''}` : bp.did,
              value: bp.did
            })),
            itemText: 'label',
            action: this.updateCLID
          },
          {
            id: 'defaultOutgoingEmailBusinessProcessId',
            titleI18nKey: 'defaultOutgoingEmailBusinessProcess',
            type: 'dropdown',
            value: agentSettings.defaultOutgoingEmailBusinessProcessId,
            items: this.availableEmailBusinessProcesses,
            action: this.updateDefaultOutgoingEmailBusinessProcessId
          },
          {
            id: 'defaultOutgoingSmsBusinessProcessId',
            titleI18nKey: 'defaultOutgoingSmsBusinessProcess',
            type: 'dropdown',
            value: agentSettings.defaultOutgoingSmsBusinessProcessId,
            items: this.availableSmsBusinessProcesses,
            action: this.updateDefaultOutgoingSmsBusinessProcessId
          },
          {
            id: 'changeExtension',
            titleI18nKey: 'popUpBtnChangeExtension',
            type: 'button',
            value: agentSettings.changeExtension || '',
            action: this.openEndpointSelectionDialog,
            disabled: !this.loggedInAgent.canChangeExtension || false
          }
        ]
      };
    },

    defaultCallingLineIdentificationPerBP() {
      const { agentSettings } = this.currentUserSettings;

      const availableBusinessProcesses = this.availableBusinessProcesses.map(bp => bp.id);
      if (!availableBusinessProcesses.includes(agentSettings.defaultOutgoingCallBusinessProcessId)) {
        return null;
      }

      const CLID = (agentSettings.defaultCallingLineIdentificationPerBP || []).find(
        config => config.businessProcessId === agentSettings.defaultOutgoingCallBusinessProcessId
      );

      return CLID ? CLID.CLID : null;
    },

    businessProcessesDIDs() {
      const { agentSettings } = this.currentUserSettings;
      return [
        { name: '', did: '' },
        ...this.getBusinessProcessesDIDs(agentSettings.defaultOutgoingCallBusinessProcessId)
      ];
    },

    incomingInteractionRingtones() {
      return [
        {
          i18nKey: 'agentSettings.incomingInteractionRingtoneOff',
          value: 'silent'
        },
        { divider: true },
        ...this.availableIncomingInteractionRingtones
      ];
    },

    filteredSettings() {
      const result = {};

      Object.entries(this.availableSettings).forEach(([key, value]) => {
        const settings = value.filter(setting => {
          return new RegExp(this.searchString, 'i').test(this.$t(`${key}.${setting.titleI18nKey}`));
        });
        if (settings.length) {
          result[key] = settings;
        }
      });

      return result;
    },

    availableEmailBusinessProcesses() {
      return this.availableBusinessProcesses
        .filter(bp => bp.outgoingMediaTypes.includes('Email'))
        .map(bp => {
          return { label: bp.name, value: bp.id };
        });
    },

    availableSmsBusinessProcesses() {
      return this.availableBusinessProcesses
        .filter(bp => bp.outgoingMediaTypes.includes('SMS'))
        .map(bp => {
          return { label: bp.name, value: bp.id };
        });
    }
  },

  methods: {
    ...mapActions('SETTINGS', [
      'updateIncomingInteractionDisplay',
      'updateAutoPlayRecordings',
      'updatePauseKPIEvents',
      'updateMuteDTMF',
      'updateAutoOffHookState',
      'updateAutoOpenInteractionDetails',
      'updateAlertEndOfBreak',
      'updateLanguage',
      'updateTimeFormat',
      'updateShowAppCurrentTime',
      'updateTimeZone',
      'updateThemeMode',
      'updateThemeId',
      'updatePinnedSideBarOnMaximizedMode',
      'updateShowApplicationTour',
      'updateIdleTimeToAgentIntelligenceCenter',
      'updateChatIncomingInteractionRingtones',
      'updateEmailIncomingInteractionRingtones',
      'updateDefaultOutgoingCallBusinessProcessId',
      'updateDefaultOutgoingEmailBusinessProcessId',
      'updateDefaultOutgoingSmsBusinessProcessId',
      'updateSmsIncomingInteractionRingtones',
      'updateInteractionHistoryPeriod',
      'updateLatestShowedFeatures',
      'updateCallIncomingInteractionRingtones'
    ]),

    ...mapActions('SETTINGS', ['updateDefaultCallingLineIdentificationPerBP']),

    updateCLID(newCLID) {
      const selectedBusinessProcessId = this.currentUserSettings.agentSettings.defaultOutgoingCallBusinessProcessId;
      const availableBusinessProcesses = this.availableBusinessProcesses.map(bp => bp.id);
      const CLIDConfig = (
        this.currentUserSettings.agentSettings.defaultCallingLineIdentificationPerBP || []
      ).filter(config => availableBusinessProcesses.includes(config.businessProcessId));
      const oldConfig = (CLIDConfig || []).find(config => config.businessProcessId === selectedBusinessProcessId);

      if (oldConfig) {
        oldConfig.CLID = newCLID;
      } else {
        CLIDConfig.push({
          businessProcessId: selectedBusinessProcessId,
          CLID: newCLID
        });
      }

      this.updateDefaultCallingLineIdentificationPerBP(CLIDConfig);
    },

    playRingtone(id) {
      switch (id) {
        case 'incomingInteractionRingtones.call':
          this.$ringtoneManager.startInteractionRingtone({ isTelephony: true }, false);
          break;
        case 'incomingInteractionRingtones.chat':
          this.$ringtoneManager.startInteractionRingtone({ isChat: true }, false);
          break;
        case 'incomingInteractionRingtones.email':
          this.$ringtoneManager.startInteractionRingtone({ isEmail: true }, false);
          break;
        case 'incomingInteractionRingtones.sms':
          this.$ringtoneManager.startInteractionRingtone({ isSms: true }, false);
          break;
        default:
          throw new Error('Invalid ringtone provided');
      }
    },

    handleSearch(searchString) {
      this.searchString = searchString;
    },

    openEndpointSelectionDialog() {
      this.$refs.endpointSelectionDialog.open();
    },

    saveSetting(value, action) {
      this.$ringtoneManager.stopInteractionRingtone(true);
      action(value);
    },

    async updateUserLanguage(language) {
      try {
        await this.updateLanguage(language);
        const reloadConfirmed = await this.$refs.languageConfirmationDialog.open();
        if (!reloadConfirmed) {
          return;
        }
        location.reload();
      } catch (e) {
        this.$root.$emit('snackbar-message', {
          message: this.$t('errors.updateLanguage'),
          type: 'error'
        });
      }
    }
  }
};
</script>

<style scoped>
.settings-content {
  white-space: normal;
}
.side-bar-item:not(:last-child) {
  border-bottom: 1px solid #0003;
}
.slider-inside {
  position: absolute;
  margin: -24px 0 0;
}
.slider {
  position: absolute;
  margin: 16px 0 0;
  width: calc(100% - 40px);
}
.dropdown {
  width: 100%;
  cursor: pointer !important;
}

.v-text-field > .v-input__control > .v-input__slot {
  cursor: pointer !important;
}

.v-input.dropdown.v-text-field.v-select {
  padding-top: 24px;
}

[tooltip]:hover {
  cursor: pointer;
  background-color: var(--v-surface-base);
}

[tooltip] .tooltip-message {
  opacity: 0;
  transform: translateY(-100%) scale(0);
  transition: all 0.3s ease;
  padding: 12px;
  width: 240px;
  background: var(--v-secondary-base);
  color: #fffd;
  border-radius: 4px;
  top: -8px;
  left: 8px;
  display: block;
  position: absolute;
}

[tooltip]:hover .tooltip-message {
  opacity: 1;
  transform: translateY(-100%) scale(1);
}
</style>
